import { anyLeague } from "../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../entities/index.js";
import { DataSection, PitchData, PitchingRankingStats, ResultData, ResultType, StatType } from "../../enums/index.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";
import sessionWithPitchingRadarData from "../../classificators/session/sessionWithPitchingRadarData.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import ResultsSlice from "../../valueObjects/ResultsSlice.js";
import resultsSliceForPitcher from "../../classificators/resultsSlice/resultsSliceForPitcher.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";
import ClassifiedPitchesFilter from "../../filters/contextual/ClassifiedPitchesFilter.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import FastballClassifier from "../../classificators/pitchTypeGroups/FastballClassifier.js";
import breakingBallClassifier from "../../classificators/pitchTypeGroups/BreakingBallClassifier.js";
import offSpeedClassifier from "../../classificators/pitchTypeGroups/OffSpeedClassifier.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";

class CumulativePitchSpinCalculator extends MultiRankingStatCalculator{
    constructor(customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }

        super(
            [
                PitchingRankingStats.AverageFastballSpin,
                PitchingRankingStats.AverageBreakingBallSpin,
                PitchingRankingStats.AverageOffSpeedSpin,
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithPitchingRadarData,
                satisfiesAll(
                    pitchingScopeContext,
                    resultsSliceForPitcher
                )
            ),
            skipCache
        );
    
        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const output = {};
        const pitches = this.filterResults(slice.getSmallestSlice(), session, league);
        const fastballStats = this.resultsProcessor.calcStats(
            [StatType.Max.key, StatType.Avg.key, StatType.Count.key],
            this.resultsProcessor.filterData(
                pitches, [{
                    parameter: ResultData.getDataPath(
                        PitchData.Classification.key, DataSection.Tagging.key, ResultType.Pitch.key
                    ),
                    operator: 'function',
                    value: FastballClassifier
                }]
            ),
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(PitchData.Spin.key, DataSection.Sensor.key, ResultType.Pitch.key)
            ) ],
            false
        );

        if (
            fastballStats && fastballStats.hasOwnProperty(PitchData.Spin.key)
            && fastballStats[PitchData.Spin.key][StatType.Count.key]
            && fastballStats[PitchData.Spin.key][StatType.Avg.key]
        ) {
            output[this.supportedParams[0].key] = RankingStat.fromParameter(
                this.supportedParams[0],
                this.numberFormatter.format(fastballStats[PitchData.Spin.key][StatType.Avg.key]),
                fastballStats[PitchData.Spin.key][StatType.Count.key]
            );
        }

        const breakingBallStats = this.resultsProcessor.calcStats(
            [StatType.Avg.key, StatType.Count.key],
            this.resultsProcessor.filterData(
                pitches, [{
                    parameter: ResultData.getDataPath(
                        PitchData.Classification.key, DataSection.Tagging.key, ResultType.Pitch.key
                    ),
                    operator: 'function',
                    value: breakingBallClassifier
                }]
            ),
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(PitchData.Spin.key, DataSection.Sensor.key, ResultType.Pitch.key)
            ) ],
            false
        );

        if (
            breakingBallStats && breakingBallStats.hasOwnProperty(PitchData.Spin.key)
            && breakingBallStats[PitchData.Spin.key][StatType.Count.key]
            && breakingBallStats[PitchData.Spin.key][StatType.Avg.key]
        ) {
            output[this.supportedParams[1].key] = RankingStat.fromParameter(
                this.supportedParams[1],
                this.numberFormatter.format(breakingBallStats[PitchData.Spin.key][StatType.Avg.key]),
                breakingBallStats[PitchData.Spin.key][StatType.Count.key]
            );
        }

        const offSpeedStats = this.resultsProcessor.calcStats(
            [StatType.Avg.key, StatType.Count.key],
            this.resultsProcessor.filterData(
                pitches, [{
                    parameter: ResultData.getDataPath(
                        PitchData.Classification.key, DataSection.Tagging.key, ResultType.Pitch.key
                    ),
                    operator: 'function',
                    value: offSpeedClassifier
                }]
            ),
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(PitchData.Spin.key, DataSection.Sensor.key, ResultType.Pitch.key)
            ) ],
            false
        );

        if (
            offSpeedStats && offSpeedStats.hasOwnProperty(PitchData.Spin.key)
            && offSpeedStats[PitchData.Spin.key][StatType.Count.key]
            && offSpeedStats[PitchData.Spin.key][StatType.Avg.key]
        ) {
            output[this.supportedParams[2].key] = RankingStat.fromParameter(
                this.supportedParams[2],
                this.numberFormatter.format(offSpeedStats[PitchData.Spin.key][StatType.Avg.key]),
                offSpeedStats[PitchData.Spin.key][StatType.Count.key]
            );
        }

        return output;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default CumulativePitchSpinCalculator;